import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { PaginationSortingQuery, PresignedStorageResource } from '../../../../../../libs/shared/nest-utils/src';
import { MessageService } from 'primeng/api';
import { AnalyticsEventsService } from '../../services/analytics-events.service';
import { FeedbacksService } from '../../services/feedback.service';
import { S3Service } from '../../services/s3.service';
import {
  AttachmentBody,
  CreateOpenFeedbackBody,
  OpenFeedbackCategoryResponse,
  PaginatedOpenFeedbackCategoryResponse,
} from '../../../../../../libs/open-feedback/config/src';
import { ToastModule } from 'primeng/toast';

enum OrderBy {
  ASC = 'asc',
  DESC = 'desc',
}

@Component({
  selector: 'open-feedback-create-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    TranslateModule,
    SelectModule,
    TextareaModule,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './open-feedback-create-dialog.html',
  styleUrls: ['./open-feedback-create-dialog.scss'],
})
export class OpenFeedbackCreateDialog implements OnInit {
  constructor(
    private translate: TranslateService,
    private readonly feedbackService: FeedbacksService,
    private readonly s3Service: S3Service,
    private readonly messageService: MessageService,
    private readonly analyticsEventsService: AnalyticsEventsService,
  ) { }

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();

  paginationSortingQuery: PaginationSortingQuery = {
    page: 1,
    size: 10000,
    getTotalCount: true,
  };

  // Form fields
  subject: string = '';
  category: string = '';
  message: string = '';
  attachedFiles: File[] = [];
  filekeys: string[] = []; // Array to hold file keys after upload
  loading: boolean = false;
  // Rimosso showSuccess: la logica ora è gestita dal componente sidebar

  // Area options (placeholder, replace with real options if needed)
  categoryOptions: { label: string; value: string }[] = [];

  getAllCategories() {
    this.loading = true;
    this.feedbackService.getAllOpenFeedbackCategories(this.paginationSortingQuery).subscribe({
      next: (response: PaginatedOpenFeedbackCategoryResponse) => {
        this.categoryOptions = response.items.map((category: OpenFeedbackCategoryResponse) => ({
          label: category.name,
          value: category.id,
        }));
        this.loading = false;
      },
      error: (error: any) => {
        console.error('Failed to load categories:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load categories. Please try again later.',
        });
        this.loading = false;
      },
    });
  }

  ngOnInit(): void {
    this.getAllCategories();
    // Track feedback open view when dialog opens
    this.analyticsEventsService.trackFeedbackOpenView();
  }

  get dialogTitle(): string {
    return 'Lascia un feedback';
  }

  get dialogWidth(): string {
    return '450px';
  }

  // File input handler (multiple files)
  onFileSelected(event: any) {
    const files: FileList = event.target.files;
    if (files && files.length > 0) {
      const availableSlots = 2 - this.attachedFiles.length;
      let added = 0;
      for (let i = 0; i < files.length && added < availableSlots; i++) {
        const file = files[i];

        const ext = file.name.split('.').pop()?.toLowerCase();
        const allowedExtensions = ['pdf', 'doc', 'docx', 'txt', 'jpg', 'jpeg', 'png', 'gif'];
        if (!allowedExtensions.includes(ext || '')) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Estensione non valida',
            detail: `Il file ${file.name} ha un'estensione non valida. Sono accettati solo i seguenti formati: ${allowedExtensions.join(', ')}.`,
          });
          continue;
        }

        const isPdf = ext === 'pdf';
        const maxSize = isPdf ? 50 * 1024 * 1024 : 2 * 1024 * 1024; // 50MB for pdf, 2MB for others
        if (file.size > maxSize) {
          this.messageService.add({
            severity: 'warn',
            summary: 'File troppo grande',
            detail: isPdf
              ? `Il file ${file.name} supera la dimensione massima di 50MB consentita per i PDF.`
              : `Il file ${file.name} supera la dimensione massima di 2MB consentita per questo tipo di file.`,
          });
          continue;
        }
        this.attachedFiles.push(file);
        added++;
      }
      if (this.attachedFiles.length >= 2 && files.length > availableSlots) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Limite raggiunto',
          detail: 'Puoi caricare al massimo 2 file.',
        });
      }
    }
  }

  removeFile(index: number) {
    this.attachedFiles.splice(index, 1);
  }

  async generateAttachmentBody(): Promise<AttachmentBody[]> {
    const attachments: AttachmentBody[] = [];
    for (const file of this.attachedFiles) {
      try {
        const presignedData: PresignedStorageResource = await new Promise((resolve, reject) => {
          this.feedbackService.generateOpenFeedbackPresignedUrl().subscribe({
            next: resolve,
            error: reject,
          });
        });
        await this.feedbackService.putPresignedAsync(file, presignedData.presignedUrl);
        attachments.push({
          key: presignedData.key,
          fileName: file.name,
        });
        this.messageService.add({
          severity: 'success',
          summary: 'File caricato',
          detail: `Il file ${file.name} è stato caricato con successo.`,
        });
      } catch (error: any) {
        console.error('Error uploading file:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore di caricamento',
          detail: `Errore durante il caricamento del file ${file.name}. Per favore, riprova.`,
        });
      }
    }
    return attachments;
  }

  // Submit feedback
  @Output() feedbackSuccess = new EventEmitter<void>();

  async onSubmitFeedback() {
    if (!this.subject || !this.category || !this.message) return;
    this.loading = true;

    const attachments: AttachmentBody[] = await this.generateAttachmentBody();

    this.feedbackService
      .createOpenFeedback({
        subject: this.subject,
        category: this.category,
        message: this.message,
        attachments: attachments,
      } as unknown as Partial<CreateOpenFeedbackBody>)
      .subscribe({
        next: (response) => {
          // Track feedback open complete
          this.analyticsEventsService.trackFeedbackOpenComplete(response.id);
          this.visible = false;
          this.feedbackSuccess.emit();
          this.messageService.add({
            severity: 'success',
            summary: 'Feedback inviato',
            detail: 'Il tuo feedback è stato inviato con successo.',
          });
        },
        error: (error: any) => {
          console.error('Error saving file:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Errore di salvataggio',
            detail: `Errore durante il salvataggio del feedback. Per favore, riprova.`,
          });
        },
      });
  }

  // Close dialog
  onCloseDialog() {
    this.resetForm();
    this.visibleChange.emit(false);
  }


  // Remove legacy confirmation logic and unused fields
  onDialogHide(): void {
    this.resetForm();
    this.visibleChange.emit(false);
  }
  // Reset all form fields
  resetForm(): void {
    this.subject = '';
    this.category = '';
    this.message = '';
    this.attachedFiles = [];
    this.filekeys = [];
    this.loading = false;
  }

  onCancel(): void {
    this.visibleChange.emit(false);
  }

  onConfirm(): void {
    this.visibleChange.emit(false);
  }

  // Handle Enter key in confirmation input
  onConfirmationKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.onConfirm();
    }
  }

  // Handle Escape key
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.onCancel();
    }
  }
}
